// KitchenScreensaver — full-screen photo roulette that fades in after
// N minutes of no user activity. Mitigates LCD image retention on the
// always-on kitchen counter Surface by cycling distinct frames every
// 30s. Pixels are spread further by drifting the time/event overlay
// position on every photo swap.
//
// Resume conditions: any pointer/key/touch event in the renderer OR a
// wake-word event delivered through window.VultureKitchen.onWake.
// While idle the wake-word listener stays running in the wake_service
// (it talks to main, which forwards through the bridge), so "Kaw Kaw"
// dismisses the screensaver and opens the quick-chat in the same step.
//
// Photo source: ~/vulture-kitchen/photos/ on the Surface, served
// through the Electron main-process custom protocol kiosk-photo://.
// Empty folder → component renders null; drop JPG/PNG/WEBP in there
// and they appear on next idle.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { api, type CalendarEvent } from "../lib/rpc";
import "./kitchen-screensaver.css";

interface Props {
  /** When non-null, only render the screensaver for this kiosk
   *  profile. We bail early when in a regular browser session. */
  kioskProfile: string | null;
}

interface KitchenBridge {
  listPhotos?: () => Promise<string[]>;
  photoUrl?: (name: string) => string;
  onWake?: (h: (p: { score: number | null; ts: number }) => void) => () => void;
  forceDisplayOn?: () => Promise<string>;
}

// Idle thresholds + rotation cadence — tuned for "kitchen" feel.
// Overridable via ?screensaver_idle=<seconds> query param so we can
// shorten the engage window during testing without redeploying.
// Default 5min to engage, 30s per photo, overlay drift every 5 photos.
function readIdleOverrideMs(): number | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("screensaver_idle");
    if (!raw) return null;
    const sec = Number(raw);
    if (!Number.isFinite(sec) || sec < 5) return null;
    return Math.floor(sec * 1000);
  } catch { return null; }
}
const IDLE_MS_DEFAULT = 5 * 60 * 1000;
const ROTATE_MS = 30 * 1000;
const OVERLAY_DRIFT_EVERY = 5;
// Activity events that "wake" the screen — kept narrow to avoid the
// scroll-driven kiosk content false-activating us. Touches/clicks +
// any keyboard.
const ACTIVITY_EVENTS = ["pointerdown", "mousedown", "touchstart", "keydown"] as const;

function hhmm(d: Date): string {
  const h = d.getHours();
  const hh12 = ((h + 11) % 12) + 1;
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh12}:${mm}`;
}

function ampm(d: Date): string {
  return d.getHours() < 12 ? "AM" : "PM";
}

function fullDate(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

function nextEventTitle(events: CalendarEvent[]): string | null {
  const now = Date.now();
  const upcoming = events
    .map((e) => ({ e, t: new Date(e.dtstart).getTime() }))
    .filter((x) => Number.isFinite(x.t) && x.t > now)
    .sort((a, b) => a.t - b.t);
  if (upcoming.length === 0) return null;
  const next = upcoming[0];
  const when = new Date(next.t).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `Next: ${next.e.title || "Event"} · ${when}`;
}

export function KitchenScreensaver({ kioskProfile }: Props) {
  const [active, setActive] = useState(false);
  const [photos, setPhotos] = useState<string[]>([]);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const lastActivityRef = useRef(Date.now());
  const rotationCountRef = useRef(0);

  const bridge: KitchenBridge | null = useMemo(() => {
    if (typeof window === "undefined") return null;
    return (window as unknown as { VultureKitchen?: KitchenBridge }).VultureKitchen || null;
  }, []);
  const idleMs = useMemo(() => readIdleOverrideMs() ?? IDLE_MS_DEFAULT, []);
  // One-time boot log so we can verify in kiosk.log that the saver
  // initialised with the expected idle window + photo count.
  useEffect(() => {
    if (kioskProfile === "kitchen") {
      console.log(`[screensaver] init kiosk=${kioskProfile} idleMs=${idleMs} bridge=${!!bridge} listPhotos=${!!bridge?.listPhotos}`);
    }
  }, [kioskProfile, idleMs, bridge]);

  // Pull the photo list on mount + every 60s after that so newly-
  // dropped pics appear without restarting the kiosk.
  useEffect(() => {
    if (kioskProfile !== "kitchen" || !bridge?.listPhotos) return;
    let cancelled = false;
    const load = async () => {
      try {
        const list = await bridge.listPhotos!();
        // Fisher-Yates shuffle so consecutive engagements don't show
        // the same album-ordered sequence. Done once per fetch.
        const shuffled = [...list];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        console.log(`[screensaver] listPhotos returned ${list.length} entries (shuffled, first: ${shuffled[0] || "<none>"})`);
        if (!cancelled) setPhotos((prev) => {
          // Membership compare so a re-shuffle of the same set doesn't churn.
          if (prev.length === list.length && prev.every((n) => list.includes(n)) && list.every((n) => prev.includes(n))) return prev;
          return shuffled;
        });
      } catch (err) { console.warn("[screensaver] listPhotos threw:", err); }
    };
    void load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [kioskProfile, bridge]);

  // Today's calendar events for the "next up" peek.
  useEffect(() => {
    if (kioskProfile !== "kitchen") return;
    let cancelled = false;
    const load = async () => {
      try {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setDate(end.getDate() + 2);
        end.setHours(0, 0, 0, 0);
        const list = await api.calendarListEvents({ rangeStart: start.toISOString(), rangeEnd: end.toISOString() });
        if (!cancelled) setEvents(list);
      } catch { /* ignore */ }
    };
    void load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [kioskProfile]);

  // Activity tracking — anything that counts as "user is here" resets
  // the idle clock and dismisses the screensaver if it's up.
  useEffect(() => {
    if (kioskProfile !== "kitchen") return;
    const onActivity = () => {
      lastActivityRef.current = Date.now();
      if (active) {
        setActive(false);
        rotationCountRef.current = 0;
      }
    };
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, true);
    }
    // Wake-word event = "user is here too" (they spoke). Force-display-on
    // first so a sleeping screen lights up, then dismiss the saver.
    let unsubscribeWake: (() => void) | null = null;
    if (bridge?.onWake) {
      unsubscribeWake = bridge.onWake(() => {
        bridge.forceDisplayOn?.();
        onActivity();
      });
    }
    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity, true);
      }
      unsubscribeWake?.();
    };
  }, [kioskProfile, active, bridge]);

  // Idle detector — checks every 10s whether to engage.
  useEffect(() => {
    if (kioskProfile !== "kitchen") return;
    const id = setInterval(() => {
      const idleFor = Date.now() - lastActivityRef.current;
      if (!active && idleFor >= idleMs && photos.length > 0) {
        console.log(`[screensaver] engaging after ${Math.round(idleFor/1000)}s idle, ${photos.length} photos`);
        setActive(true);
        setPhotoIdx(Math.floor(Math.random() * photos.length));
      }
    }, 10_000);
    return () => clearInterval(id);
  }, [active, kioskProfile, photos.length, idleMs]);

  // Photo rotation + clock tick. Single interval drives both so
  // overlay text + photo stay in sync on the same frame.
  useEffect(() => {
    if (!active) return;
    const tick = () => {
      setNow(new Date());
    };
    const clockId = setInterval(tick, 30_000);
    const rotateId = setInterval(() => {
      setPhotoIdx((i) => (photos.length === 0 ? 0 : (i + 1) % photos.length));
      rotationCountRef.current += 1;
    }, ROTATE_MS);
    tick();
    return () => { clearInterval(clockId); clearInterval(rotateId); };
  }, [active, photos.length]);

  if (kioskProfile !== "kitchen" || !active || photos.length === 0) return null;

  const photoName = photos[photoIdx % photos.length];
  const photoSrc = bridge?.photoUrl ? bridge.photoUrl(photoName) : `kiosk-photo://${encodeURIComponent(photoName)}`;
  const upcoming = nextEventTitle(events);
  // Overlay corner drifts every OVERLAY_DRIFT_EVERY rotations between
  // the four corners. Static text in a static corner is exactly what
  // we're trying to avoid.
  const cornerIdx = Math.floor(rotationCountRef.current / OVERLAY_DRIFT_EVERY) % 4;
  const corner = (["bl", "tr", "tl", "br"] as const)[cornerIdx];

  return (
    <div className="kitchen-screensaver" data-testid="kitchen-screensaver">
      <img
        key={photoName}
        className="kitchen-screensaver-photo"
        src={photoSrc}
        alt=""
        aria-hidden
      />
      <div className={`kitchen-screensaver-overlay corner-${corner}`}>
        <div className="kss-clock">
          <span className="kss-clock-hhmm">{hhmm(now)}</span>
          <span className="kss-clock-ampm">{ampm(now)}</span>
        </div>
        <div className="kss-date">{fullDate(now)}</div>
        {upcoming && <div className="kss-next">{upcoming}</div>}
      </div>
    </div>
  );
}
